/**
 * Utilitários para trabalhar com faixas de preços por convidados
 */

import { PriceRange } from "../types/productTypes";
import { safeParsePrice } from "./priceUtils";
import { parseDateFromStorage } from "@/utils/dateUtils";
import { getTotalPriceAdjustmentMultiplier, parseAdjustmentData } from "./pricingAdjustmentUtils";

export interface PricingRangeData {
  minGuests: number;
  maxGuests: number;
  pricePerPerson: number;
  basePrice?: number;
  name?: string;
  description?: string;
  minBilledGuests?: number; // Para lógica de mínimo faturado
}

/**
 * Valida a estrutura de uma faixa de preços
 */
export const validatePriceRange = (range: any): boolean => {
  if (!range || typeof range !== 'object') return false;
  
  const { minGuests, maxGuests, pricePerPerson } = range;
  
  // Verificar campos obrigatórios
  if (minGuests === undefined || maxGuests === undefined || pricePerPerson === undefined) {
    return false;
  }
  
  // Verificar tipos e valores
  const min = Number(minGuests);
  const max = Number(maxGuests);
  const price = Number(pricePerPerson);
  
  if (isNaN(min) || isNaN(max) || isNaN(price)) return false;
  if (min <= 0 || max <= 0 || price <= 0) return false;
  if (min > max) return false;
  
  return true;
};

/**
 * Valida um array completo de faixas de preços
 */
export const validatePriceRanges = (ranges: any[]): boolean => {
  if (!Array.isArray(ranges) || ranges.length === 0) return false;
  
  // Validar cada faixa individualmente
  for (const range of ranges) {
    if (!validatePriceRange(range)) return false;
  }
  
  // Verificar se não há sobreposições
  const sortedRanges = ranges.slice().sort((a, b) => a.minGuests - b.minGuests);
  
  for (let i = 1; i < sortedRanges.length; i++) {
    const prev = sortedRanges[i - 1];
    const current = sortedRanges[i];
    
    if (prev.maxGuests >= current.minGuests) {
      console.warn('Sobreposição de faixas detectada:', prev, current);
      return false;
    }
  }
  
  return true;
};

/**
 * Encontra a faixa de preços adequada para um número específico de convidados
 */
export const findPriceRangeForGuests = (
  priceRanges: PricingRangeData[],
  guestCount: number
): PricingRangeData | null => {
  if (!priceRanges || priceRanges.length === 0) return null;
  
  const validRanges = priceRanges.filter(validatePriceRange);
  
  return validRanges.find(range => 
    guestCount >= range.minGuests && guestCount <= range.maxGuests
  ) || null;
};

/**
 * Calcula o preço total baseado na faixa de preços e número de convidados
 */
export const calculatePriceFromRange = (
  priceRange: PricingRangeData,
  guestCount: number
): number => {
  if (!priceRange || !validatePriceRange(priceRange)) return 0;
  
  const basePrice = safeParsePrice(priceRange.basePrice || 0);
  const pricePerPerson = safeParsePrice(priceRange.pricePerPerson);
  
  // Aplicar lógica de mínimo faturado se definido
  const effectiveGuests = priceRange.minBilledGuests 
    ? Math.max(guestCount, priceRange.minBilledGuests)
    : guestCount;
  
  return basePrice + (pricePerPerson * effectiveGuests);
};

const resolveEventDate = (value?: Date | string | null): Date | null => {
  if (!value) return null;
  if (value instanceof Date && !isNaN(value.getTime())) return value;
  if (typeof value === 'string') {
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return parseDateFromStorage(value);
    const parsed = new Date(value);
    return isNaN(parsed.getTime()) ? null : parsed;
  }
  return null;
};

/**
 * Faixa de preço para o total de PESSOAS (convidados + crianças/staff). Se esse total passar da última faixa (ex.: 295
 * convidados + 10 staff numa tabela que vai até 300), vale a faixa dos convidados: nunca cai em "sem faixa" (preço 0).
 */
const findRangeForHeadcount = (
  priceRanges: PricingRangeData[],
  guestCount: number,
  rangeGuestCount?: number
): PricingRangeData | null =>
  (rangeGuestCount && rangeGuestCount > 0 ? findPriceRangeForGuests(priceRanges, rangeGuestCount) : null)
  ?? findPriceRangeForGuests(priceRanges, guestCount);

/**
 * Calcula o preço para um produto com faixas especiais.
 * `rangeGuestCount` = pessoas que definem a FAIXA (convidados + crianças/staff); o total continua cobrando só os `guestCount`
 * convidados, e crianças/staff pagam seu percentual à parte.
 */
export const calculateProductPriceWithRanges = (
  product: any,
  guestCount: number,
  platformTax?: number,
  eventDateInput?: Date | string | null,
  eventType?: string | null,
  rangeGuestCount?: number
): number => {
  const { yearlyAdjustments, seasonalGroups, eventTypeAdjustments } = parseAdjustmentData(product);
  const eventDate = resolveEventDate(eventDateInput ?? product?.event_date ?? product?.eventDate ?? product?.date);
  const resolvedEventType = eventType ?? product?.event_type ?? product?.eventType ?? null;
  const adjustmentMultiplier = getTotalPriceAdjustmentMultiplier(
    yearlyAdjustments,
    seasonalGroups,
    eventDate,
    eventTypeAdjustments,
    resolvedEventType
  );

  let pricePerPerson = 0;
  let basePrice = 0;

  const rawSpecialPrice = product?.special_price ?? product?.price_ranges;
  const normalizedSpecialPrice = typeof rawSpecialPrice === 'string'
    ? (() => {
        try {
          return JSON.parse(rawSpecialPrice);
        } catch {
          return null;
        }
      })()
    : rawSpecialPrice;

  if (!product.special_pricing || !normalizedSpecialPrice) {
    // When special_pricing is false, the price is a FIXED TOTAL (not per-person)
    // Return it directly, applying only adjustments and tax
    const rawPrice = product.price;
    const fixedPrice = typeof rawPrice === 'number' && !isNaN(rawPrice) ? rawPrice : safeParsePrice(rawPrice || 0);
    
    let adjustedPrice = fixedPrice;
    if (adjustmentMultiplier !== 1) {
      adjustedPrice = Math.round(fixedPrice * adjustmentMultiplier * 100) / 100;
    }
    
    if (platformTax && platformTax > 0) {
      adjustedPrice = Math.round(adjustedPrice * (1 + platformTax / 100) * 100) / 100;
    }
    

    return adjustedPrice;
  }

  let priceRanges: PricingRangeData[] = [];

  if (Array.isArray(normalizedSpecialPrice)) {
    priceRanges = normalizedSpecialPrice;
  } else if (typeof normalizedSpecialPrice === 'object') {
    priceRanges = [normalizedSpecialPrice as PricingRangeData];
  }

  const matchingRange = findRangeForHeadcount(priceRanges, guestCount, rangeGuestCount);

  if (matchingRange) {
    pricePerPerson = safeParsePrice(matchingRange.pricePerPerson);
    basePrice = safeParsePrice(matchingRange.basePrice || 0);

    const effectiveGuests = matchingRange.minBilledGuests
      ? Math.max(guestCount, matchingRange.minBilledGuests)
      : guestCount;

    guestCount = effectiveGuests;
    

  } else {
    pricePerPerson = safeParsePrice(product.price || 0);
  }

  const originalPricePerPerson = pricePerPerson;
  
  if (adjustmentMultiplier !== 1) {
    pricePerPerson = Math.round(pricePerPerson * adjustmentMultiplier * 100) / 100;
    basePrice = Math.round(basePrice * adjustmentMultiplier * 100) / 100;

  }

  if (platformTax && platformTax > 0) {
    const pricePerPersonWithTax = pricePerPerson * (1 + platformTax / 100);
    const roundedPricePerPersonWithTax = Math.round(pricePerPersonWithTax * 100) / 100;
    return Math.round((basePrice + (roundedPricePerPersonWithTax * guestCount)) * 100) / 100;
  }

  const total = basePrice + (pricePerPerson * guestCount);

  return Math.round(total * 100) / 100;
};

/**
 * Obtém informações sobre as faixas de preços de um produto
 */
export const getProductPricingInfo = (product: any): {
  hasPricingRanges: boolean;
  minGuestsSupported: number | null;
  maxGuestsSupported: number | null;
  rangeCount: number;
  pricingType: 'flat_rate' | 'range_based' | 'custom';
} => {
  const hasPricingRanges = Boolean(product.special_pricing && product.special_price);
  
  if (!hasPricingRanges) {
    return {
      hasPricingRanges: false,
      minGuestsSupported: null,
      maxGuestsSupported: null,
      rangeCount: 0,
      pricingType: 'flat_rate'
    };
  }
  
  let priceRanges: PricingRangeData[] = [];
  
  if (Array.isArray(product.special_price)) {
    priceRanges = product.special_price.filter(validatePriceRange);
  } else if (typeof product.special_price === 'object' && validatePriceRange(product.special_price)) {
    priceRanges = [product.special_price];
  }
  
  if (priceRanges.length === 0) {
    return {
      hasPricingRanges: false,
      minGuestsSupported: null,
      maxGuestsSupported: null,
      rangeCount: 0,
      pricingType: 'flat_rate'
    };
  }
  
  const minGuestsSupported = Math.min(...priceRanges.map(r => r.minGuests));
  const maxGuestsSupported = Math.max(...priceRanges.map(r => r.maxGuests));
  
  return {
    hasPricingRanges: true,
    minGuestsSupported,
    maxGuestsSupported,
    rangeCount: priceRanges.length,
    pricingType: priceRanges.length === 1 ? 'custom' : 'range_based'
  };
};

/**
 * Gera faixas de preços padrão baseadas em um preço base
 */
export const generateDefaultPriceRanges = (basePrice: number): PricingRangeData[] => {
  const pricePerPerson = basePrice * 0.1; // 10% do preço base por pessoa
  
  return [
    { minGuests: 1, maxGuests: 10, pricePerPerson: pricePerPerson * 1.5, basePrice },
    { minGuests: 11, maxGuests: 30, pricePerPerson: pricePerPerson * 1.2, basePrice },
    { minGuests: 31, maxGuests: 50, pricePerPerson, basePrice },
    { minGuests: 51, maxGuests: 100, pricePerPerson: pricePerPerson * 0.8, basePrice },
    { minGuests: 101, maxGuests: 200, pricePerPerson: pricePerPerson * 0.6, basePrice }
  ];
};

/**
 * Busca produtos que atendem a uma faixa específica de convidados
 */
export const findProductsForGuestRange = (
  products: any[],
  minGuests: number,
  maxGuests: number
): any[] => {
  return products.filter(product => {
    const pricingInfo = getProductPricingInfo(product);
    
    if (!pricingInfo.hasPricingRanges) {
      // Produtos sem faixas específicas atendem qualquer quantidade
      return true;
    }
    
    // Verificar se há sobreposição com a faixa desejada
    return (
      pricingInfo.minGuestsSupported !== null &&
      pricingInfo.maxGuestsSupported !== null &&
      pricingInfo.minGuestsSupported <= maxGuests &&
      pricingInfo.maxGuestsSupported >= minGuests
    );
  });
};

/**
 * Converte faixas de preços para formato de exibição
 */
export const formatPriceRangesForDisplay = (
  priceRanges: PricingRangeData[]
): string[] => {
  if (!priceRanges || priceRanges.length === 0) return [];
  
  return priceRanges
    .filter(validatePriceRange)
    .sort((a, b) => a.minGuests - b.minGuests)
    .map(range => {
      const priceFormatted = new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL'
      }).format(range.pricePerPerson);
      
      if (range.minGuests === range.maxGuests) {
        return `${range.minGuests} convidados: ${priceFormatted}/pessoa`;
      } else {
        return `${range.minGuests}-${range.maxGuests} convidados: ${priceFormatted}/pessoa`;
      }
    });
};

/**
 * Preço por convidado já ajustado (anual/sazonal/tipo de evento + taxa da plataforma).
 * Usado pelo box "Preço especial" para calcular quanto crianças/staff pagam.
 */
export const getAdjustedPricePerPerson = (
  product: any,
  guestCount: number,
  platformTax?: number,
  eventDateInput?: Date | string | null,
  eventType?: string | null,
  rangeGuestCount?: number
): number => {
  const { yearlyAdjustments, seasonalGroups, eventTypeAdjustments } = parseAdjustmentData(product);
  const eventDate = resolveEventDate(eventDateInput ?? product?.event_date ?? product?.eventDate ?? product?.date);
  const resolvedEventType = eventType ?? product?.event_type ?? product?.eventType ?? null;
  const adjustmentMultiplier = getTotalPriceAdjustmentMultiplier(
    yearlyAdjustments,
    seasonalGroups,
    eventDate,
    eventTypeAdjustments,
    resolvedEventType
  );

  const rawSpecialPrice = product?.special_price ?? product?.price_ranges;
  const normalizedSpecialPrice = typeof rawSpecialPrice === 'string'
    ? (() => { try { return JSON.parse(rawSpecialPrice); } catch { return null; } })()
    : rawSpecialPrice;

  let pricePerPerson = safeParsePrice(product?.price || 0);

  if (product?.special_pricing && normalizedSpecialPrice) {
    const ranges: PricingRangeData[] = Array.isArray(normalizedSpecialPrice)
      ? normalizedSpecialPrice
      : [normalizedSpecialPrice as PricingRangeData];
    const matching = findRangeForHeadcount(ranges, guestCount, rangeGuestCount);
    if (matching) pricePerPerson = safeParsePrice(matching.pricePerPerson);
  }

  if (adjustmentMultiplier !== 1) {
    pricePerPerson = Math.round(pricePerPerson * adjustmentMultiplier * 100) / 100;
  }

  if (platformTax && platformTax > 0) {
    pricePerPerson = Math.round(pricePerPerson * (1 + platformTax / 100) * 100) / 100;
  }

  return Math.round(pricePerPerson * 100) / 100;
};
